import { Dtos, UserId } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { agentPreviewsQueryKey, agentUserPreviewsQueryKey } from "../user.keys";

export const useCancelAgentUserInvitation = () => {
	const { _delete, get } = useRequest();
	const queryClient = useQueryClient();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const mutation = useMutation({
		mutationKey: ["cancelAgentUserInvitation"],
		mutationFn: async (agentId: UserId) => {
			await _delete(`/v2/agents/${agentId}/invite`, undefined, "none");
		},
		onMutate: async (agentId: UserId) => {
			const previousData = queryClient.getQueryData(agentPreviewsQueryKey());
			queryClient.setQueryData(
				agentPreviewsQueryKey(),
				(old: Dtos.Agent.UserPreview.Get.Response[] | undefined) => {
					if (!old) return [];
					return old.filter((agentPreview) => agentPreview.id !== agentId);
				},
			);
			return { previousData };
		},
		onSuccess: async () => {
			queryClient.invalidateQueries({ queryKey: agentUserPreviewsQueryKey() });
			const updatedAgentPreviews =
				await get<Dtos.Agent.UserPreview.Get.Response[]>(`/v2/agents/user-previews`);
			queryClient.setQueryData(agentPreviewsQueryKey(), updatedAgentPreviews);
		},
		onError: (error: Error, _variables, context) => {
			if (!!context) {
				queryClient.setQueryData(agentPreviewsQueryKey(), context.previousData);
			}
			logError(error);
			triggerErrorModal(error);
		},
	});

	return {
		cancelAgentUserInvitation: mutation.mutate,
		cancelAgentUserInvitationIsLoading: mutation.isPending,
		cancelAgentUserInvitationError: mutation.error,
	};
};
